import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { ApiRequest, IApiresponse, MileageRateStatusEnum, RequestVehicleTypes } from '../../../_models';
import { CloseFormAction, OrgOwnershipTypeInfo, OrgOwnershipTypeResponse, OrgUKMileageRateInfo } from '../../../_models/advance-mileage/org-ukmileage-rate-info';
import { Claims } from '../../../_models/common/claims';
import { ClaimsService } from '../../../_services/common/claims.service';
import { UkMileageRateSettingService } from '../../../_services/advance-mileage/uk-mileage-rate-setting.service';
import { OrganisationSettingService } from '../../../_services/organisation-setting/organisation-setting.service';
import { environment } from '../../../../environments/environment';

export interface FmInfoEditPanelConfig {
  quickChangeAction: string;
  wizardRoute: string;
  wizardQueryParams?: any;
  panelTitle?: string;
  contextData?: any;
}

@Component({
    selector: 'fm-info-edit-panel',
    templateUrl: './fm-info-edit-panel.component.html',
    styleUrls: ['./fm-info-edit-panel.component.scss'],
    standalone: false
})
export class FmInfoEditPanelComponent implements OnInit {
  @Input() show: boolean = false;
  @Input() config: FmInfoEditPanelConfig;
  @Output() closePanelEvent = new EventEmitter<boolean>();
  @Output() quickChangeEvent = new EventEmitter<any>();

  claimsSubscription: Subscription;
  claims: Claims;

  // Mileage rate panel properties
  showEditMileageRate: boolean = false;
  MileageRateFormData: any;
  ownershipTypeList = <OrgOwnershipTypeInfo[]>[];
  GetOrgOwnershipTypeResponse: any;
  setupMileageRatePanel: any;

  // Receipt type setup properties
  showReceiptTypeSetup: boolean = false;
  vatNoVatReceiptDetails: any;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private claimsService: ClaimsService,
    private cdr: ChangeDetectorRef,
    private ukMileageRateSettingService: UkMileageRateSettingService,
    private organisationSettingService: OrganisationSettingService
  ) {
    this.claimsSubscription = this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
    });
  }

  ngOnInit(): void {
    // Initialize component
  }

  ngOnDestroy(): void {
    if (this.claimsSubscription) {
      this.claimsSubscription.unsubscribe();
    }
  }

  // Close the panel
  fnClosePanel() {
    this.closePanelEvent.emit(false);
  }

  // Handle link clicks
  fnHandleLinkClick(action: string) {
    if (action === 'quick-change') {
      this.handleQuickChange();
    } else if (action === 'wizard') {
      this.navigateToWizard();
      this.fnClosePanel();
    }
  }

  // Handle quick change action
  private handleQuickChange() {
    if (this.config.quickChangeAction === 'mileage-rate') {
      this.GetOrgOwnershipTypeData();
    } else if (this.config.quickChangeAction === 'receiptTypeSetup') {
      // Fetch receipt type data if not already loaded
      this.getReceiptTypeSettings();
    } else {
      // Emit for other components to handle
      this.quickChangeEvent.emit(this.config.quickChangeAction);
    }
  }

  // Navigate to wizard
  private navigateToWizard() {
    if (this.config.wizardRoute) {
      this.router.navigate([this.config.wizardRoute], {
        queryParams: this.config.wizardQueryParams || {}
      });
    }
  }

  // Mileage rate specific functionality
  GetOrgOwnershipTypeData() {
    // this.cdr.detectChanges();
    if (this.config.contextData && this.config.contextData.model && 
        this.config.contextData.model.mileageMultipleDateDetails && 
        this.config.contextData.model.mileageMultipleDateDetails.length > 0 &&
        this.config.contextData.model.mileageMultipleDateDetails[0].mileageRateInfo && 
        this.config.contextData.model.mileageMultipleDateDetails[0].mileageRateInfo.length > 0 &&
        this.config.contextData.model.mileageMultipleDateDetails[0].mileageRateInfo[0].orgVehicleTypeMapId
    ) {
      let emplistreq = new ApiRequest<RequestVehicleTypes>(
        "GetOrgOwnershipType",
        environment.applicationVersion,
        environment.requestId
      );
      let emplistreqinfo = new RequestVehicleTypes();
      emplistreqinfo.rateType = this.claims.RateType;
      emplistreq.requestInfo = emplistreqinfo;
      
      this.ukMileageRateSettingService
        .GetOrgOwnershipType(emplistreq)
        .subscribe((res: IApiresponse<OrgOwnershipTypeResponse>) => {
          this.ownershipTypeList = res.responseInfo.orgOwnershipTypeList;
          this.GetOrgOwnershipTypeResponse = res.responseInfo;
          //this.cdr.detectChanges();
          this.openEditMileageRates();
        });
    }
  }

  fnPreparePanelData(paramsData: any) {
    let orgUKMileageRateDetail = new OrgUKMileageRateInfo();
    orgUKMileageRateDetail.effectiveFrom = paramsData.effectiveFrom;
    orgUKMileageRateDetail.statusCode = MileageRateStatusEnum.Active;
    this.MileageRateFormData = {
      isOverlayRequired: true,
      rowData: paramsData,
      isCopyIconHide: true,
      highlightEditRate: true,
    };
    //this.cdr.detectChanges();
    this.showEditMileageRate = true;
    this.cdr.detectChanges();
  }

  fnCloseQuickPanel(closeFormAction: CloseFormAction) {
    this.showEditMileageRate = false;
    this.showReceiptTypeSetup = false;
    if (closeFormAction && closeFormAction.isRefreshRequired) {
      // Refresh any data if needed
    }
  }

  openEditMileageRates() {
    const model = this.config.contextData?.model;
    if (model && model.mileageMultipleDateDetails && model.mileageMultipleDateDetails.length > 0) {
      const orgVehicleTypeMapId = model.mileageMultipleDateDetails[0].mileageRateInfo[0].orgVehicleTypeMapId;
      
      this.setupMileageRatePanel = {
        isFromExternalPanel: true,
        orgVehicleTypeMapId: orgVehicleTypeMapId || 0,
        highlightEditRate: true,
        currentFinancialYear: this.GetOrgOwnershipTypeResponse?.currentFinancialYear
      };

      let ownerShip = this.ownershipTypeList.find(
        (a) => a.orgVehicleTypeMapId == orgVehicleTypeMapId && a.statusValue =='Active'
      );

      if (ownerShip) {
        //this.cdr.detectChanges();
        this.fnPreparePanelData(ownerShip);
      } else {
        this.fnPreparePanelData({
          effectiveFrom: new Date(),
          orgVehicleTypeMapId: 0
        });
      }
    } else {
      this.fnPreparePanelData({
        effectiveFrom: new Date(),
        orgVehicleTypeMapId: 0
      });
    }
  }

  // Receipt type settings
  getReceiptTypeSettings() {
    this.organisationSettingService
      .getVatNoVatReceiptType()
      .pipe(first())
      .subscribe(
        (data) => {
          if (data.responseInfo != null) {
            this.vatNoVatReceiptDetails = data.responseInfo.receiptTypes.map(e => this.mapReceiptType(e));    
            this.showReceiptTypeSetup = true; 
          }
        },
        (error) => {
          console.error('Error fetching receipt type settings:', error);
        }
      );
  }

  mapReceiptType(e): any {
    let receiptTypeDetail: any = e;
    receiptTypeDetail.deriveName = e.receiptType;    
    receiptTypeDetail.isSwitchedOn = e.isDisplayOn;
    receiptTypeDetail.isSwitchedOn2 = e.isPolicyViolationOn;
    receiptTypeDetail.informationId = e.vatNoVatReceiptTypeId;
    receiptTypeDetail.isHidden = e.sequenceNumber == 1? true : false;
    receiptTypeDetail.showText = true;
    receiptTypeDetail.showSwitchText = this.translate.instant('displayed');
    receiptTypeDetail.showSwitchText2 = this.translate.instant('applied');
    return receiptTypeDetail;
  }
}
